import { motion } from "framer-motion";
import { Zap, Shield, Users } from "lucide-react";

const WelcomeScreen = ({ onBegin, heroCount }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen flex flex-col items-center justify-center relative px-6"
      data-testid="welcome-screen"
    >
      {/* Background glow effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-neon-purple/10 rounded-full blur-[150px]" />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-neon-cyan/5 rounded-full blur-[100px]" />
      </div>

      {/* Top right logo */}
      <div className="absolute top-6 right-6 z-10">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-2"
        >
          <Shield className="w-6 h-6 text-neon-purple" />
          <span className="font-orbitron text-sm tracking-widest text-white/70">SCROBITS AI</span>
        </motion.div>
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center max-w-3xl mx-auto">
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.8 }}
          className="mb-8"
        >
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center border border-white/10 animate-float">
            <Zap className="w-12 h-12 text-neon-purple" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-orbitron text-4xl sm:text-5xl lg:text-6xl font-bold text-white text-glow mb-6 tracking-wider uppercase"
        >
          Discover Your
          <br />
          <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-neon-cyan bg-clip-text text-transparent">
            Superhero Identity
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="font-rajdhani text-lg sm:text-xl text-white/60 mb-12 tracking-wide"
        >
          Powered by AI. Built by Scrobits.
        </motion.p>

        {/* Begin Scan Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBegin}
          className="relative group px-12 py-5 bg-transparent border-2 border-neon-purple text-neon-purple font-orbitron text-lg tracking-widest uppercase rounded-sm overflow-hidden transition-all duration-300 hover:text-white hover:border-neon-purple animate-pulse-glow"
          data-testid="begin-scan-btn"
        >
          {/* Button background on hover */}
          <span className="absolute inset-0 bg-neon-purple transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
          
          {/* Button text */}
          <span className="relative z-10 flex items-center gap-3">
            <Zap className="w-5 h-5" />
            Begin Scan
          </span>
        </motion.button>
      </div>

      {/* Bottom hero counter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        data-testid="hero-counter"
      >
        <div className="flex items-center gap-3 glass px-6 py-3 rounded-full">
          <Users className="w-5 h-5 text-neon-cyan" />
          <span className="font-rajdhani text-base text-white/80">
            <span className="font-bold text-neon-cyan">{heroCount}</span> heroes created today
          </span>
        </div>
      </motion.div>

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-2 h-2 bg-neon-purple rounded-full animate-pulse opacity-50" />
      <div className="absolute top-40 right-20 w-1 h-1 bg-neon-cyan rounded-full animate-pulse opacity-70" />
      <div className="absolute bottom-40 left-20 w-1.5 h-1.5 bg-neon-pink rounded-full animate-pulse opacity-60" />
      <div className="absolute bottom-60 right-10 w-2 h-2 bg-neon-purple rounded-full animate-pulse opacity-40" />
    </motion.div>
  );
};

export default WelcomeScreen;
